/**
 * Every block and mark of a block document, defined once (plan §8.6). The validator, tiptap
 * attributes, insert palette, Markdown export and HTML import are generated from this list.
 *
 * Existing node types keep their stored shape (every stored page stays valid). The page editor
 * (wave 2) opens tables, toggles (details), embeds, tables of contents, mentions, text colour and
 * highlight; file, inline value and barcode stay model-only until their editor support lands (no
 * `palette` entry, refused by `BlockDocument`). Every block of the `block` group may also carry an
 * element id in `attrs.id` (see `carriesElementId` in the registry).
 *
 * Owner: doc-engine (wave 1), page-editor (wave 2).
 */
import { z } from 'zod';
import { FieldPath as DottedFieldPath } from '../filter';
import { ComponentInputs } from '../custom-components';
import { FormSlug } from '../forms';
import { T_BOOLEAN, T_NUMBER, T_TEXT, type ExprSite } from './expr-site';
import {
  defineBlock,
  defineMark,
  type BlockDefinition,
  type HtmlElementView,
  type MarkDefinition,
} from './block-kit';
import { CalculationTable, CanvasFrame, CanvasItem, RecordValue } from './canvas-v1';
import { codeExprSites, codeProps, refineCode } from './codes';
import { DataBlockQuery, DataResult } from './data-block';
import { bindingExprSites, prefixSites } from './binding';
import { insightExprSites } from './insight';
import {
  LiveViewBlockAttrs,
  InsightBlockAttrs,
  InsightSnapshotAttrs,
  RepeaterBlockAttrs,
  PageBlockVisibility,
} from './page-data';
import { DesignSettings, VisualElement, imageSource } from './design';
import { documentText, safeDocumentUrl, type DocumentNode } from './node';
import { NAMED_COLORS, THEME_COLOR_ROLES, TextAlign, cssColorToHex, nearestNamedColor } from './color';

const ObjectKeyRe = /^[a-z][a-z0-9_]{0,62}$/;
const Condition = z.string().max(1000).nullish();
const textAlign = { textAlign: TextAlign.nullish() };

function alignFrom(el: HtmlElementView): Record<string, unknown> {
  const raw = (el.style['text-align'] ?? el.attrs.align ?? '').toLowerCase();
  return ['center', 'right', 'justify'].includes(raw) ? { textAlign: raw } : {};
}

const conditionSite = (source: string | null | undefined, key = 'visibleWhen'): ExprSite[] =>
  source ? [{ path: [key], source, role: 'condition', context: 'record', expected: T_BOOLEAN }] : [];

/** Indent every line after the first (list items, quotes). */
function hang(marker: string, body: string): string {
  const pad = ' '.repeat(marker.length);
  return (
    marker +
    body
      .split('\n')
      .map((line, i) => (i === 0 || !line ? line : pad + line))
      .join('\n')
  );
}

const escapeCell = (s: string) => s.replace(/\|/g, '\\|').replace(/\n+/g, ' ').trim();

// -------------------------------------------------------------------------------------------------
// Repair helpers (children are already repaired; see `repairDocument`)
// -------------------------------------------------------------------------------------------------

const EMPTY_PARAGRAPH: DocumentNode = { type: 'paragraph' };

/** List items start with a paragraph. */
function startWithParagraph(node: DocumentNode): DocumentNode {
  const children = node.content ?? [];
  return children[0]?.type === 'paragraph' ? node : { ...node, content: [EMPTY_PARAGRAPH, ...children] };
}

/** Lists hold items only: a stray block becomes an item of its own; an empty list unwraps. */
const listOf =
  (item: string) =>
  (node: DocumentNode): DocumentNode | null => {
    const items = (node.content ?? []).map((child) =>
      child.type === item
        ? child
        : { type: item, content: child.type === 'paragraph' ? [child] : [EMPTY_PARAGRAPH, child] },
    );
    return items.length ? { ...node, content: items } : null;
  };

/** A paragraph holding one link (degraded embeds and files). */
function linkParagraph(text: string, href: string): DocumentNode {
  if (!text) return EMPTY_PARAGRAPH;
  return safeDocumentUrl(href)
    ? {
        type: 'paragraph',
        content: [
          {
            type: 'text',
            text,
            marks: [
              {
                type: 'link',
                attrs: { href, target: '_blank', rel: 'noopener noreferrer nofollow', class: null },
              },
            ],
          },
        ],
      }
    : { type: 'paragraph', content: [{ type: 'text', text }] };
}

/** Text of the cells of a table row, for degraded tables. */
const rowText = (row: DocumentNode) =>
  (row.content ?? []).map((cell) => documentText(cell).replace(/\n+/g, ' ').trim()).join(' | ');

// -------------------------------------------------------------------------------------------------
// Structure and text
// -------------------------------------------------------------------------------------------------

/**
 * Page-level reading options of a block document (page-editor, wave 2): full width, small text and a
 * lock that keeps the page from accidental edits (it stays editable through ⋯ › Unlock).
 */
export const PageLayout = z
  .object({
    fullWidth: z.boolean().optional(),
    smallText: z.boolean().optional(),
    locked: z.boolean().optional(),
  })
  .strict();
export type PageLayout = z.infer<typeof PageLayout>;

const doc = defineBlock({
  name: 'doc',
  supported: true,
  attrs: z.object({ design: DesignSettings.optional(), layout: PageLayout.optional() }).strict(),
  defaults: {},
  content: '(block* | canvasPage{1,60})',
  publish: 'live',
  aiDescription: 'The document root.',
  label: { en: 'Document', de: 'Dokument' },
  validate: (node, _attrs, ctx) => {
    const frames = (node.content ?? []).filter((n) => n?.type === 'canvasPage');
    if (frames.length && (frames.length !== node.content?.length || frames.length > 60))
      ctx.fail('Use between one and sixty canvas pages', 'doc.canvas_pages');
  },
});

const paragraph = defineBlock({
  name: 'paragraph',
  supported: true,
  attrs: z.object(textAlign).strict(),
  defaults: {},
  content: 'inline*',
  group: 'block',
  publish: 'live',
  fromHTML: [{ tag: 'p', attrs: alignFrom }],
  toMarkdown: (node, ctx) => ctx.inline(node),
  aiDescription: 'Body text with inline formatting, links, mentions and inline values.',
  label: { en: 'Text', de: 'Text' },
  description: { en: 'Body copy with inline formatting', de: 'Fließtext mit Formatierung' },
  category: 'writing',
  palette: { order: 10, compact: true },
});

const text = defineBlock({
  name: 'text',
  supported: true,
  attrs: z.object({}).strict(),
  defaults: {},
  group: 'inline',
  inline: true,
  publish: 'live',
  aiDescription: 'A run of text; formatting is expressed with marks.',
  label: { en: 'Text', de: 'Text' },
});

const hardBreak = defineBlock({
  name: 'hardBreak',
  supported: true,
  attrs: z.object({}).strict(),
  defaults: {},
  group: 'inline',
  inline: true,
  publish: 'live',
  fromHTML: [{ tag: 'br' }],
  aiDescription: 'A line break inside a paragraph.',
  label: { en: 'Line break', de: 'Zeilenumbruch' },
});

const heading = defineBlock({
  name: 'heading',
  supported: true,
  attrs: z.object({ level: z.union([z.literal(1), z.literal(2), z.literal(3)]), ...textAlign }).strict(),
  defaults: { level: 1 },
  content: 'inline*',
  group: 'block',
  publish: 'live',
  fromHTML: [1, 2, 3, 4, 5, 6].map((n) => ({
    tag: `h${n}`,
    attrs: (el: HtmlElementView) => ({ level: Math.min(n, 3), ...alignFrom(el) }),
  })),
  toMarkdown: (node, ctx) => `${'#'.repeat(Number(node.attrs?.level ?? 1))} ${ctx.inline(node)}`,
  aiDescription: 'A heading of level 1, 2 or 3; use level 1 once for the main title.',
  label: { en: 'Heading', de: 'Überschrift' },
  description: { en: 'Three levels of document hierarchy', de: 'Drei Gliederungsebenen' },
  category: 'writing',
  palette: { order: 20, compact: true },
});

const bulletList = defineBlock({
  name: 'bulletList',
  supported: true,
  attrs: z.object({}).strict(),
  defaults: {},
  content: 'listItem+',
  group: 'block',
  publish: 'live',
  fromHTML: [{ tag: 'ul', match: (el) => el.attrs['data-type'] !== 'taskList' }],
  toMarkdown: (node, ctx) => (node.content ?? []).map((li) => hang('- ', ctx.blocks(li.content))).join('\n'),
  aiDescription: 'An unordered list of listItem nodes.',
  label: { en: 'Bulleted list', de: 'Aufzählung' },
  description: { en: 'Unordered items', de: 'Ungeordnete Punkte' },
  category: 'writing',
  palette: { order: 30, compact: true },
  repair: listOf('listItem'),
});

/** How an ordered list counts (the HTML `type`): 1, 2, 3 · a, b, c · A, B, C · i, ii, iii · I, II, III. */
export const ListMarker = z.enum(['1', 'a', 'A', 'i', 'I']);
export type ListMarker = z.infer<typeof ListMarker>;

const orderedList = defineBlock({
  name: 'orderedList',
  supported: true,
  attrs: z
    .object({ start: z.number().int().min(0).max(100000).default(1), type: ListMarker.nullish() })
    .strict(),
  defaults: { start: 1 },
  content: 'listItem+',
  group: 'block',
  publish: 'live',
  fromHTML: [
    {
      tag: 'ol',
      attrs: (el) => {
        const marker = ListMarker.safeParse(el.attrs.type);
        return {
          ...(Number(el.attrs.start) > 1 ? { start: Number(el.attrs.start) } : {}),
          ...(marker.success && marker.data !== '1' ? { type: marker.data } : {}),
        };
      },
    },
  ],
  toMarkdown: (node, ctx) => {
    const start = Number(node.attrs?.start ?? 1);
    return (node.content ?? []).map((li, i) => hang(`${start + i}. `, ctx.blocks(li.content))).join('\n');
  },
  aiDescription: 'A numbered list of listItem nodes.',
  label: { en: 'Numbered list', de: 'Nummerierte Liste' },
  description: { en: 'Sequential instructions', de: 'Schritte in Reihenfolge' },
  category: 'writing',
  palette: { order: 40, compact: true },
  repair: listOf('listItem'),
});

const listItem = defineBlock({
  name: 'listItem',
  supported: true,
  attrs: z.object({}).strict(),
  defaults: {},
  content: 'paragraph block*',
  publish: 'live',
  fromHTML: [{ tag: 'li', match: (el) => el.attrs['data-type'] !== 'taskItem' }],
  aiDescription: 'An item of a bulleted or numbered list; starts with a paragraph.',
  label: { en: 'List item', de: 'Listenpunkt' },
  repair: startWithParagraph,
});

const taskList = defineBlock({
  name: 'taskList',
  supported: true,
  attrs: z.object({}).strict(),
  defaults: {},
  content: 'taskItem+',
  group: 'block',
  publish: 'live',
  fromHTML: [{ tag: 'ul', match: (el) => el.attrs['data-type'] === 'taskList', priority: 60 }],
  toMarkdown: (node, ctx) =>
    (node.content ?? [])
      .map((li) => hang(li.attrs?.checked ? '- [x] ' : '- [ ] ', ctx.blocks(li.content)))
      .join('\n'),
  aiDescription: 'A checklist of taskItem nodes.',
  label: { en: 'Checklist', de: 'Checkliste' },
  description: { en: 'Checkable tasks', de: 'Abhakbare Aufgaben' },
  category: 'writing',
  palette: { order: 50, compact: true },
  repair: listOf('taskItem'),
});

const taskItem = defineBlock({
  name: 'taskItem',
  supported: true,
  attrs: z.object({ checked: z.boolean().default(false) }).strict(),
  defaults: { checked: false },
  content: 'paragraph block*',
  publish: 'live',
  fromHTML: [
    {
      tag: 'li',
      match: (el) => el.attrs['data-type'] === 'taskItem',
      attrs: (el) => ({ checked: el.attrs['data-checked'] === 'true' }),
      priority: 60,
    },
  ],
  aiDescription: 'A checklist item with `checked`.',
  label: { en: 'Checklist item', de: 'Checklistenpunkt' },
  repair: startWithParagraph,
});

const blockquote = defineBlock({
  name: 'blockquote',
  supported: true,
  attrs: z.object({}).strict(),
  defaults: {},
  content: 'block+',
  group: 'block',
  publish: 'live',
  fromHTML: [{ tag: 'blockquote' }],
  toMarkdown: (node, ctx) =>
    ctx
      .blocks(node.content)
      .split('\n')
      .map((line) => (line ? `> ${line}` : '>'))
      .join('\n'),
  aiDescription: 'A quoted passage containing blocks.',
  label: { en: 'Quote', de: 'Zitat' },
  description: { en: 'A quoted passage', de: 'Ein zitierter Abschnitt' },
  category: 'writing',
  palette: { order: 60, compact: true },
});

const codeBlock = defineBlock({
  name: 'codeBlock',
  supported: true,
  attrs: z
    .object({
      language: z
        .string()
        .max(40)
        .regex(/^[\w#+.-]*$/)
        .nullish(),
    })
    .strict(),
  defaults: {},
  content: 'text*',
  group: 'block',
  marks: '',
  publish: 'live',
  fromHTML: [
    {
      tag: 'pre',
      attrs: (el) => {
        const lang =
          (el.attrs['data-language'] ?? el.classes.find((c) => c.startsWith('language-'))?.slice(9)) || null;
        return lang && /^[\w#+.-]{1,40}$/.test(lang) ? { language: lang } : {};
      },
    },
  ],
  toMarkdown: (node, ctx) => {
    const body = ctx.text(node).replace(/\n$/, '');
    const fence = body.includes('```') ? '~~~' : '```';
    return `${fence}${node.attrs?.language ?? ''}\n${body}\n${fence}`;
  },
  aiDescription: 'A plain code sample, never executed.',
  label: { en: 'Code', de: 'Code' },
  description: { en: 'A plain code sample, never executed', de: 'Ein Codebeispiel, wird nie ausgeführt' },
  category: 'writing',
  palette: { order: 70, compact: true },
});

const horizontalRule = defineBlock({
  name: 'horizontalRule',
  supported: true,
  attrs: z.object({}).strict(),
  defaults: {},
  group: 'block',
  publish: 'live',
  fromHTML: [{ tag: 'hr' }],
  toMarkdown: () => '---',
  aiDescription: 'A visual separator.',
  label: { en: 'Divider', de: 'Trennlinie' },
  description: { en: 'A visual separator', de: 'Eine visuelle Trennung' },
  category: 'layout',
  palette: { order: 80, compact: true },
});

// -------------------------------------------------------------------------------------------------
// Layout
// -------------------------------------------------------------------------------------------------

const callout = defineBlock({
  name: 'callout',
  supported: true,
  attrs: z
    .object({
      tone: z.enum(['info', 'success', 'warning', 'danger', 'neutral']).nullish(),
      icon: z.string().max(16).nullish(),
    })
    .strict(),
  defaults: {},
  content: 'block+',
  group: 'block',
  publish: 'live',
  fromHTML: [{ tag: 'aside' }, { tag: 'div', match: (el) => 'data-callout' in el.attrs, priority: 60 }],
  toMarkdown: (node, ctx) => {
    const kind =
      { success: 'TIP', warning: 'WARNING', danger: 'CAUTION' }[String(node.attrs?.tone)] ?? 'NOTE';
    return [
      `> [!${kind}]`,
      ...ctx
        .blocks(node.content)
        .split('\n')
        .map((l) => (l ? `> ${l}` : '>')),
    ].join('\n');
  },
  aiDescription:
    'Important information containing other blocks; `tone` info, success, warning, danger or neutral.',
  label: { en: 'Callout', de: 'Hinweis' },
  description: {
    en: 'Important information containing other blocks',
    de: 'Wichtige Information mit weiteren Blöcken',
  },
  category: 'layout',
  palette: { order: 90, compact: true },
});

const toggle = defineBlock({
  name: 'toggle',
  supported: true,
  attrs: z.object({ title: z.string().max(160) }).strict(),
  defaults: { title: 'Details' },
  content: 'block+',
  group: 'block',
  publish: 'live',
  fromHTML: [{ tag: 'details', match: (el) => 'data-toggle' in el.attrs, priority: 60 }],
  toMarkdown: (node, ctx) => `**${String(node.attrs?.title ?? '')}**\n\n${ctx.blocks(node.content)}`,
  aiDescription: 'Collapsible content with an editable title.',
  label: { en: 'Toggle', de: 'Aufklappbereich' },
  description: { en: 'Collapsible content with an editable title', de: 'Einklappbarer Inhalt mit Titel' },
  category: 'layout',
  deprecated: { replacedBy: 'details' },
});

const details = defineBlock({
  name: 'details',
  supported: true,
  attrs: z.object({ open: z.boolean().default(false) }).strict(),
  defaults: { open: false },
  content: 'detailsSummary detailsContent',
  group: 'block',
  publish: 'live',
  fromHTML: [{ tag: 'details', attrs: (el) => ({ open: 'open' in el.attrs }) }],
  toMarkdown: (node, ctx) => {
    const [summary, body] = node.content ?? [];
    return `**${summary ? ctx.inline(summary) : ''}**\n\n${ctx.blocks(body?.content)}`;
  },
  aiDescription: 'A toggle: a one-line summary that expands to show blocks (detailsSummary, detailsContent).',
  label: { en: 'Toggle', de: 'Aufklappbereich' },
  description: { en: 'Collapsible blocks under a summary line', de: 'Einklappbare Blöcke unter einer Zeile' },
  category: 'layout',
  palette: { order: 100, compact: true },
  repair: (node) => {
    const children = node.content ?? [];
    const hasSummary = children[0]?.type === 'detailsSummary';
    const summary: DocumentNode = hasSummary ? children[0]! : { type: 'detailsSummary' };
    const blocks = (hasSummary ? children.slice(1) : children).flatMap((c): DocumentNode[] =>
      c.type === 'detailsContent'
        ? (c.content ?? [])
        : c.type === 'detailsSummary'
          ? [{ type: 'paragraph', ...(c.content ? { content: c.content } : {}) }]
          : [c],
    );
    return {
      ...node,
      content: [summary, { type: 'detailsContent', content: blocks.length ? blocks : [EMPTY_PARAGRAPH] }],
    };
  },
  degrade: (node) => {
    const [summary, body] = node.content ?? [];
    return [
      { type: 'paragraph', ...(summary?.content ? { content: summary.content } : {}) },
      ...(body?.content ?? []),
    ];
  },
});

const detailsSummary = defineBlock({
  name: 'detailsSummary',
  supported: true,
  attrs: z.object({}).strict(),
  defaults: {},
  content: 'inline*',
  publish: 'live',
  fromHTML: [{ tag: 'summary' }],
  aiDescription: 'The always visible line of a toggle.',
  label: { en: 'Toggle summary', de: 'Zusammenfassung' },
});

const detailsContent = defineBlock({
  name: 'detailsContent',
  supported: true,
  attrs: z.object({}).strict(),
  defaults: {},
  content: 'block+',
  publish: 'live',
  aiDescription: 'The blocks inside a toggle.',
  label: { en: 'Toggle content', de: 'Aufklappinhalt' },
});

const spacer = defineBlock({
  name: 'spacer',
  supported: true,
  attrs: z.object({ size: z.enum(['small', 'medium', 'large']) }).strict(),
  defaults: { size: 'medium' },
  group: 'block',
  publish: 'live',
  toMarkdown: () => '',
  aiDescription: 'Small, medium or large vertical spacing.',
  label: { en: 'Spacer', de: 'Abstand' },
  description: {
    en: 'Small, medium or large vertical spacing',
    de: 'Kleiner, mittlerer oder großer Abstand',
  },
  category: 'layout',
  palette: { order: 110, compact: true },
});

const ColumnRatio = z.enum(['equal', 'wide-center', 'wide-left', 'wide-right']);
const columns = defineBlock({
  name: 'columns',
  supported: true,
  attrs: z
    .object({
      ratio: ColumnRatio.optional(),
      visibleWhen: Condition,
      /** Column widths as fractions that add up to 1 (overrides `ratio`). */
      widths: z.array(z.number().min(0.1).max(0.9)).min(2).max(4).nullish(),
    })
    .strict()
    .refine((a) => !a.widths || Math.abs(a.widths.reduce((s, w) => s + w, 0) - 1) < 0.01, {
      message: 'Column widths must add up to 100 %',
      path: ['widths'],
    }),
  defaults: { ratio: 'equal', visibleWhen: null },
  content: 'column{2,4}',
  group: 'block',
  publish: 'live',
  expressions: (a) => conditionSite(a.visibleWhen),
  toMarkdown: (node, ctx) => (node.content ?? []).map((c) => ctx.blocks(c.content)).join('\n\n'),
  aiDescription: 'Two to four side-by-side columns of blocks; stacks on phones.',
  label: { en: 'Columns', de: 'Spalten' },
  description: {
    en: 'Two or three columns with adjustable proportions',
    de: 'Zwei oder drei Spalten mit anpassbaren Breiten',
  },
  category: 'layout',
  palette: { order: 140 },
  contentMessage: 'Columns need two to four sections',
  validate: (node, attrs, ctx) => {
    if (attrs.widths && attrs.widths.length !== (node.content ?? []).length)
      ctx.fail('Give every column a width', 'columns.widths');
  },
  repair: (node) => {
    const columns = (node.content ?? []).filter((c) => c.type === 'column');
    if (columns.length < 2 || columns.length > 4 || columns.length !== node.content?.length) return null;
    const widths = node.attrs?.widths;
    return Array.isArray(widths) && widths.length !== columns.length
      ? { ...node, attrs: { ...node.attrs, widths: null } }
      : node;
  },
});

const column = defineBlock({
  name: 'column',
  supported: true,
  attrs: z.object({}).strict(),
  defaults: {},
  content: 'block+',
  publish: 'live',
  aiDescription: 'One column of a columns block.',
  label: { en: 'Column', de: 'Spalte' },
});

const section = defineBlock({
  name: 'section',
  supported: true,
  attrs: z
    .object({
      title: z.string().max(160),
      tone: z.enum(['plain', 'subtle', 'accent']).optional(),
      visibleWhen: Condition,
    })
    .strict(),
  defaults: { title: '', tone: 'plain', visibleWhen: null },
  content: 'block+',
  group: 'block',
  publish: 'live',
  fromHTML: [{ tag: 'section', match: (el) => 'data-section' in el.attrs, attrs: () => ({ title: '' }) }],
  expressions: (a) => conditionSite(a.visibleWhen),
  toMarkdown: (node, ctx) =>
    [node.attrs?.title ? `## ${String(node.attrs.title)}` : '', ctx.blocks(node.content)]
      .filter(Boolean)
      .join('\n\n'),
  aiDescription: 'A titled content area with plain, subtle or accent tone; also splits a page into slides.',
  label: { en: 'Section', de: 'Abschnitt' },
  description: {
    en: 'Titled content area with plain, subtle or accent tone',
    de: 'Titelbereich mit schlichtem, dezentem oder betontem Stil',
  },
  category: 'layout',
  palette: { order: 150 },
});

const tabs = defineBlock({
  name: 'tabs',
  supported: true,
  attrs: z.object({ visibleWhen: Condition }).strict(),
  defaults: { visibleWhen: null },
  content: 'tab{1,12}',
  group: 'block',
  publish: 'live',
  expressions: (a) => conditionSite(a.visibleWhen),
  toMarkdown: (node, ctx) =>
    (node.content ?? [])
      .map((t) => `### ${String(t.attrs?.title ?? '')}\n\n${ctx.blocks(t.content)}`)
      .join('\n\n'),
  aiDescription: 'Named switchable panels (tab nodes) containing any blocks.',
  label: { en: 'Tabs', de: 'Reiter' },
  description: {
    en: 'Named switchable panels containing any blocks',
    de: 'Benannte Reiter mit beliebigen Blöcken',
  },
  category: 'layout',
  palette: { order: 160 },
  contentMessage: 'Tabs need between one and twelve panels',
  repair: (node) => {
    const tabs = (node.content ?? []).filter((c) => c.type === 'tab');
    return tabs.length >= 1 && tabs.length <= 12 && tabs.length === node.content?.length ? node : null;
  },
  degrade: (node) =>
    (node.content ?? []).flatMap((t) => [
      ...(t.attrs?.title
        ? [{ type: 'heading', attrs: { level: 3 }, content: [{ type: 'text', text: String(t.attrs.title) }] }]
        : []),
      ...(t.content ?? []),
    ]),
});

const tab = defineBlock({
  name: 'tab',
  supported: true,
  attrs: z.object({ title: z.string().max(80) }).strict(),
  defaults: { title: 'Tab' },
  content: 'block+',
  publish: 'live',
  aiDescription: 'One panel of a tabs block with a title.',
  label: { en: 'Tab', de: 'Reiter' },
});

// -------------------------------------------------------------------------------------------------
// Tables
// -------------------------------------------------------------------------------------------------

const table = defineBlock({
  name: 'table',
  supported: true,
  attrs: z.object({}).strict(),
  defaults: {},
  content: 'tableRow+',
  group: 'block',
  publish: 'live',
  fromHTML: [{ tag: 'table' }],
  toMarkdown: (node, ctx) => {
    const rows = (node.content ?? []).map((row) =>
      (row.content ?? []).map((cell) => escapeCell(ctx.text(cell))),
    );
    const width = Math.max(1, ...rows.map((r) => r.length));
    const pad = (r: string[]) => [...r, ...Array<string>(width - r.length).fill('')];
    const [head = [], ...body] = rows;
    // Column alignment from the paragraphs of the first row (GFM `:---:` and `---:`).
    const align = Array.from({ length: width }, (_, i) => {
      const a = node.content?.[0]?.content?.[i]?.content?.[0]?.attrs?.textAlign;
      return a === 'center' ? ':---:' : a === 'right' ? '---:' : '---';
    });
    return [
      `| ${pad(head).join(' | ')} |`,
      `| ${align.join(' | ')} |`,
      ...body.map((r) => `| ${pad(r).join(' | ')} |`),
    ].join('\n');
  },
  toText: (node, ctx) =>
    (node.content ?? [])
      .map((row) =>
        (row.content ?? []).map((cell) => ctx.children(cell).trim().replace(/\n+/g, ' ')).join(' | '),
      )
      .join('\n'),
  aiDescription: 'A simple table of tableRow nodes; the first row may use tableHeader cells.',
  label: { en: 'Table', de: 'Tabelle' },
  description: { en: 'Rows and columns of text', de: 'Zeilen und Spalten mit Text' },
  category: 'writing',
  palette: { order: 75 },
  repair: (node) => {
    // Stray cells and blocks become rows; rows are padded to the widest row (spans counted).
    const rows = (node.content ?? []).flatMap((child): DocumentNode[] =>
      child.type === 'tableRow'
        ? [child]
        : [
            {
              type: 'tableRow',
              content: [
                child.type === 'tableCell' || child.type === 'tableHeader'
                  ? child
                  : { type: 'tableCell', content: [child] },
              ],
            },
          ],
    );
    if (!rows.length) return null;
    const width = (row: DocumentNode) =>
      (row.content ?? []).reduce((sum, cell) => sum + Math.max(1, Number(cell.attrs?.colspan ?? 1)), 0);
    const widest = Math.max(...rows.map(width));
    return {
      ...node,
      content: rows.map((row) => {
        const missing = widest - width(row);
        return missing > 0
          ? {
              ...row,
              content: [
                ...(row.content ?? []),
                ...Array.from({ length: missing }, (): DocumentNode => ({
                  type: 'tableCell',
                  content: [EMPTY_PARAGRAPH],
                })),
              ],
            }
          : row;
      }),
    };
  },
  degrade: (node) =>
    (node.content ?? [])
      .map(rowText)
      .filter((t) => t.replace(/[|\s]/g, ''))
      .map((t) => ({ type: 'paragraph', content: [{ type: 'text', text: t }] })),
});

const tableRow = defineBlock({
  name: 'tableRow',
  supported: true,
  attrs: z.object({}).strict(),
  defaults: {},
  content: '(tableCell | tableHeader)+',
  publish: 'live',
  fromHTML: [{ tag: 'tr' }],
  aiDescription: 'A row of table cells.',
  label: { en: 'Table row', de: 'Tabellenzeile' },
  repair: (node) => {
    const cells = (node.content ?? []).map((child) =>
      child.type === 'tableCell' || child.type === 'tableHeader'
        ? child
        : { type: 'tableCell', content: [child] },
    );
    return cells.length ? { ...node, content: cells } : null;
  },
});

const cellAttrs = z
  .object({
    colspan: z.number().int().min(1).max(50).default(1),
    rowspan: z.number().int().min(1).max(500).default(1),
    /** Column widths in px (one per spanned column), set by resizing. */
    colwidth: z.array(z.number().int().min(20).max(4000)).max(50).nullish(),
    background: z.enum(NAMED_COLORS).nullish(),
  })
  .strict();
const cellFromHtml = (el: HtmlElementView) => {
  const n = (v: string | undefined) => (v && /^\d+$/.test(v) && Number(v) > 1 ? Number(v) : undefined);
  const colspan = n(el.attrs.colspan),
    rowspan = n(el.attrs.rowspan);
  return { ...(colspan ? { colspan } : {}), ...(rowspan ? { rowspan } : {}) };
};

const tableCell = defineBlock({
  name: 'tableCell',
  supported: true,
  attrs: cellAttrs,
  defaults: { colspan: 1, rowspan: 1 },
  content: 'block+',
  publish: 'live',
  fromHTML: [{ tag: 'td', attrs: cellFromHtml }],
  aiDescription: 'A table cell containing blocks (usually one paragraph).',
  label: { en: 'Table cell', de: 'Tabellenzelle' },
});

const tableHeader = defineBlock({
  name: 'tableHeader',
  supported: true,
  attrs: cellAttrs,
  defaults: { colspan: 1, rowspan: 1 },
  content: 'block+',
  publish: 'live',
  fromHTML: [{ tag: 'th', attrs: cellFromHtml }],
  aiDescription: 'A header cell of a table.',
  label: { en: 'Header cell', de: 'Kopfzelle' },
});

// -------------------------------------------------------------------------------------------------
// Media and navigation
// -------------------------------------------------------------------------------------------------

const Crop = z
  .object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    w: z.number().min(0.01).max(1),
    h: z.number().min(0.01).max(1),
  })
  .strict()
  .refine((c) => c.x + c.w <= 1.0001 && c.y + c.h <= 1.0001, 'The crop must stay inside the image');

const image = defineBlock({
  name: 'image',
  supported: true,
  attrs: z
    .object({
      src: imageSource,
      fit: z.enum(['contain', 'cover']).optional(),
      position: z.enum(['center', 'top', 'bottom', 'left', 'right']).optional(),
      caption: z.string().max(300).optional(),
      /** Asset of the image library (preferred over `src` for uploads). */
      assetId: z.uuid().nullish(),
      alt: z.string().max(500).nullish(),
      /** Width in percent of the text column. */
      width: z.number().min(10).max(100).nullish(),
      align: z.enum(['left', 'center', 'right', 'full']).nullish(),
      crop: Crop.nullish(),
    })
    .strict(),
  defaults: { src: '', caption: '', fit: 'contain', position: 'center' },
  group: 'block',
  atom: true,
  publish: 'live',
  fromHTML: [
    {
      tag: 'img',
      attrs: (el) => ({
        src: el.attrs.src ?? '',
        ...(el.attrs.alt ? { alt: el.attrs.alt.slice(0, 500) } : {}),
        ...(el.attrs.title ? { caption: el.attrs.title.slice(0, 300) } : {}),
      }),
    },
  ],
  toMarkdown: (node) => {
    const a = node.attrs ?? {};
    const src = String(a.src ?? '');
    return src ? `![${String(a.alt ?? a.caption ?? '')}](${src})` : '';
  },
  toText: (node) => String(node.attrs?.alt ?? node.attrs?.caption ?? ''),
  aiDescription: 'Library asset or image URL with crop, focus and caption.',
  label: { en: 'Image', de: 'Bild' },
  description: {
    en: 'Library asset or image URL with crop, focus and caption',
    de: 'Bild aus der Bibliothek oder per URL, mit Zuschnitt und Bildunterschrift',
  },
  category: 'media',
  palette: { order: 120, compact: true },
});

const file = defineBlock({
  name: 'file',
  attrs: z
    .object({
      assetId: z.uuid(),
      name: z.string().min(1).max(255),
      size: z.number().int().min(0).max(5_000_000_000),
      mime: z
        .string()
        .max(120)
        .regex(/^[\w.+-]+\/[\w.+-]+$/),
    })
    .strict(),
  defaults: { assetId: '', name: '', size: 0, mime: '' },
  requiresSetup: true,
  group: 'block',
  atom: true,
  publish: 'live',
  toMarkdown: (node) =>
    `[${String(node.attrs?.name ?? '')}](/api/v1/assets/${String(node.attrs?.assetId ?? '')})`,
  toText: (node) => String(node.attrs?.name ?? ''),
  aiDescription: 'An attached file from the asset store, shown with its name and size.',
  label: { en: 'File', de: 'Datei' },
  description: { en: 'An attached file to download', de: 'Eine angehängte Datei zum Herunterladen' },
  category: 'media',
  degrade: (node) => [
    linkParagraph(String(node.attrs?.name ?? ''), `/api/v1/assets/${String(node.attrs?.assetId ?? '')}`),
  ],
});

const EMBED_PROVIDERS = ['youtube', 'vimeo', 'loom', 'figma', 'maps', 'generic'] as const;
const embed = defineBlock({
  name: 'embed',
  supported: true,
  attrs: z
    .object({
      url: z
        .string()
        .max(2000)
        .regex(/^https:\/\/[^\s\\]+$/i, { message: 'Embeds need an https:// address' }),
      provider: z.enum(EMBED_PROVIDERS).default('generic'),
      aspect: z.enum(['16:9', '4:3', '1:1', '3:4']).default('16:9'),
      caption: z.string().max(300).nullish(),
    })
    .strict(),
  defaults: { url: '', provider: 'generic', aspect: '16:9' },
  requiresSetup: true,
  group: 'block',
  atom: true,
  publish: 'live',
  fromHTML: [
    {
      tag: 'iframe',
      match: (el) => /^https:\/\//i.test(el.attrs.src ?? ''),
      attrs: (el) => ({ url: el.attrs.src }),
    },
  ],
  toMarkdown: (node) => `<${String(node.attrs?.url ?? '')}>`,
  toText: (node) => String(node.attrs?.url ?? ''),
  aiDescription: 'An embedded video, design or map from an https URL.',
  label: { en: 'Embed', de: 'Einbettung' },
  description: { en: 'Video, design file or map from a link', de: 'Video, Design oder Karte per Link' },
  category: 'media',
  palette: { order: 125 },
  degrade: (node) => [
    linkParagraph(String(node.attrs?.caption || node.attrs?.url || ''), String(node.attrs?.url ?? '')),
  ],
});

const pageLink = defineBlock({
  name: 'pageLink',
  supported: true,
  attrs: z
    .object({
      href: z.string().max(2000),
      label: z.string().max(200),
      /** What the link points to, so the editor and renderer show its current title. */
      ref: z
        .discriminatedUnion('kind', [
          z.object({ kind: z.literal('page'), id: z.uuid() }).strict(),
          z
            .object({ kind: z.literal('record'), id: z.uuid(), objectKey: z.string().regex(ObjectKeyRe) })
            .strict(),
        ])
        .nullish(),
      /** Shown as a card (default) or as a button. */
      style: z.enum(['card', 'button']).nullish(),
    })
    .strict(),
  defaults: { href: '/pages', label: 'Open page' },
  group: 'block',
  atom: true,
  publish: 'live',
  toMarkdown: (node) => `[${String(node.attrs?.label ?? '')}](${String(node.attrs?.href ?? '')})`,
  toText: (node) => String(node.attrs?.label ?? ''),
  aiDescription: 'A button that navigates to an existing page, record, form or external URL.',
  label: { en: 'Link to page', de: 'Link auf Seite' },
  description: {
    en: 'A card that opens another page or a record',
    de: 'Eine Karte, die eine andere Seite oder einen Datensatz öffnet',
  },
  category: 'navigation',
  palette: { order: 170 },
  validate: (_node, attrs, ctx) => {
    if (!safeDocumentUrl(attrs.href)) ctx.fail('Invalid page link', 'pageLink.href');
  },
});

const tableOfContents = defineBlock({
  name: 'tableOfContents',
  supported: true,
  attrs: z.object({ maxLevel: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(3) }).strict(),
  defaults: { maxLevel: 3 },
  group: 'block',
  atom: true,
  publish: 'live',
  toMarkdown: () => '',
  aiDescription: 'A live table of contents built from the headings of the page.',
  label: { en: 'Table of contents', de: 'Inhaltsverzeichnis' },
  description: { en: 'Links to the headings of the page', de: 'Links zu den Überschriften der Seite' },
  category: 'navigation',
  palette: { order: 175 },
});

const MentionDate = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})?)?$/;
const mention = defineBlock({
  name: 'mention',
  supported: true,
  attrs: z
    .object({
      kind: z.enum(['member', 'page', 'record', 'date']),
      /** Member, page or record id; an ISO date for `date`. */
      id: z.string().min(1).max(64),
      /** Object of a record mention. */
      objectKey: z.string().regex(ObjectKeyRe).nullish(),
      /** Title when the mention was made (shown if the target is not readable). */
      label: z.string().max(200).default(''),
    })
    .strict()
    .superRefine((a, ctx) => {
      if (a.kind === 'date' ? !MentionDate.test(a.id) : !z.uuid().safeParse(a.id).success)
        ctx.addIssue({
          code: 'custom',
          path: ['id'],
          message: a.kind === 'date' ? 'Use an ISO date' : 'Invalid id',
        });
      if ((a.kind === 'record') !== Boolean(a.objectKey))
        ctx.addIssue({ code: 'custom', path: ['objectKey'], message: 'Record mentions name their object' });
    }),
  defaults: { kind: 'member', id: '', label: '' },
  requiresSetup: true,
  group: 'inline',
  inline: true,
  atom: true,
  publish: 'snapshot',
  toMarkdown: (node) => `@${String(node.attrs?.label || node.attrs?.id || '')}`,
  toText: (node) => `@${String(node.attrs?.label || node.attrs?.id || '')}`,
  aiDescription: 'An inline mention of a member, page, record or date; members are notified.',
  label: { en: 'Mention', de: 'Erwähnung' },
  category: 'navigation',
});

// -------------------------------------------------------------------------------------------------
// Data and visuals
// -------------------------------------------------------------------------------------------------

const liveView = defineBlock({
  name: 'liveView',
  supported: true,
  attrs: LiveViewBlockAttrs,
  defaults: { binding: null, title: '', display: 'table', hideEmpty: false },
  requiresSetup: true,
  group: 'block',
  atom: true,
  publish: 'snapshot',
  expressions: (a) => [
    ...conditionSite(a.visibleWhen),
    ...(a.binding ? prefixSites(['binding'], bindingExprSites(a.binding)) : []),
  ],
  toText: (node) => String(node.attrs?.title ?? ''),
  aiDescription:
    'A live table or list using a typed query or related-record DataBinding. Ask for a source; never invent an object.',
  label: { en: 'Live view', de: 'Live-Ansicht' },
  category: 'data',
  palette: { order: 180 },
});
const pageInsight = defineBlock({
  name: 'insight',
  supported: true,
  attrs: InsightBlockAttrs,
  defaults: { insight: null, title: '' },
  requiresSetup: true,
  group: 'block',
  atom: true,
  publish: 'snapshot',
  expressions: (a) => [
    ...conditionSite(a.visibleWhen),
    ...(a.insight ? prefixSites(['insight'], insightExprSites(a.insight)) : []),
  ],
  toText: (node) => String(node.attrs?.title ?? ''),
  aiDescription:
    'A chart or metric using the shared typed Insight model. Measures, dimensions, time windows and formats are validated.',
  label: { en: 'Chart', de: 'Diagramm' },
  category: 'data',
  palette: { order: 181 },
});
const pageMetric = defineBlock({
  ...pageInsight,
  name: 'insightMetric',
  label: { en: 'Metric', de: 'Kennzahl' },
  palette: { order: 182 },
});
const insightSnapshot = defineBlock({
  name: 'insightSnapshot',
  supported: true,
  attrs: InsightSnapshotAttrs,
  defaults: { title: '' },
  requiresSetup: true,
  group: 'block',
  atom: true,
  publish: 'live',
  toText: (node) => String(node.attrs?.title ?? ''),
  aiDescription: 'A read-only evaluated Insight snapshot created by the publisher or exporter.',
  label: { en: 'Chart snapshot', de: 'Diagrammstand' },
});
const repeater = defineBlock({
  name: 'repeater',
  supported: true,
  attrs: RepeaterBlockAttrs,
  defaults: { binding: null, title: '', columns: 1 },
  requiresSetup: true,
  group: 'block',
  content: 'block+',
  publish: 'snapshot',
  expressions: (a) => [
    ...conditionSite(a.visibleWhen),
    ...(a.binding ? prefixSites(['binding'], bindingExprSites(a.binding)) : []),
  ],
  toText: (node) => String(node.attrs?.title ?? ''),
  aiDescription:
    'Repeat nested page blocks for each record from a bounded DataBinding; child record values use that row.',
  label: { en: 'Repeater', de: 'Wiederholung' },
  category: 'data',
  palette: { order: 183 },
});

const dataView = defineBlock({
  name: 'dataView',
  supported: true,
  attrs: z
    .object({
      objectKey: z.string().regex(ObjectKeyRe, { message: 'Choose a data source' }),
      viewId: z.string().nullish(),
      relationPath: DottedFieldPath.optional(),
      mode: z.enum(['table', 'count', 'cards', 'chart', 'metric']).optional(),
      query: DataBlockQuery.nullish(),
      public: z.boolean().optional(),
      title: z.string().max(160).optional(),
      visibleWhen: PageBlockVisibility,
    })
    .strict(),
  defaults: { objectKey: '', viewId: null, mode: 'table', query: null, public: false, title: '' },
  requiresSetup: true,
  group: 'block',
  atom: true,
  publish: 'snapshot',
  expressions: (a) => conditionSite(a.visibleWhen),
  toMarkdown: (node) => (node.attrs?.title ? `*${String(node.attrs.title)}*` : ''),
  toText: (node) => String(node.attrs?.title ?? ''),
  aiDescription:
    'Filtered, sorted or grouped query; table, cards, count, metric or chart. Use for live values.',
  label: { en: 'Live data', de: 'Live-Daten' },
  description: {
    en: 'Filtered, sorted or grouped query; table, cards, count, metric or chart. Use for live values.',
    de: 'Gefilterte, sortierte oder gruppierte Abfrage als Tabelle, Karten, Anzahl, Kennzahl oder Diagramm.',
  },
  category: 'data',
  palette: { order: 180 },
});

const recordValue = defineBlock({
  name: 'recordValue',
  supported: true,
  attrs: RecordValue.safeExtend({ visibleWhen: PageBlockVisibility }),
  defaults: { field: '', label: '', public: false },
  requiresSetup: true,
  group: 'block',
  atom: true,
  publish: 'snapshot',
  expressions: (a) =>
    [
      ...conditionSite(a.visibleWhen),
      ...(a.expression
        ? [{ path: ['expression'], source: a.expression, role: 'expression', context: 'record' }]
        : []),
    ] as ExprSite[],
  toText: (node) => String(node.attrs?.label ?? ''),
  aiDescription:
    'Display a field or multi-hop relationship value, or calculate an expression with dates and totals; reusable in templates',
  label: { en: 'Record value', de: 'Datensatzwert' },
  description: {
    en: 'Display a field or multi-hop relationship value, or calculate an expression with dates and totals; reusable in templates',
    de: 'Zeigt ein Feld oder einen Wert über Beziehungen oder berechnet einen Ausdruck; in Vorlagen wiederverwendbar',
  },
  category: 'data',
  palette: { order: 190 },
});

const inlineValue = defineBlock({
  name: 'inlineValue',
  supported: true,
  attrs: z
    .object({
      field: z.union([DottedFieldPath, z.literal('')]).default(''),
      expression: z.string().trim().max(4000).nullish(),
      // `line`, `lines` and `city` are for composite values (an address): one line, one item per line.
      format: z
        .enum(['auto', 'text', 'number', 'currency', 'percent', 'date', 'datetime', 'line', 'lines', 'city'])
        .nullish(),
      label: z.string().max(100).default(''),
      fallback: z.string().max(200).nullish(),
      currency: z
        .string()
        .regex(/^[A-Z]{3}$/)
        .optional(),
      decimals: z.number().int().min(0).max(8).optional(),
      public: z.boolean().default(false),
      visibleWhen: PageBlockVisibility,
    })
    .strict()
    .refine((a) => Boolean(a.field || a.expression), {
      message: 'Choose a field or enter an expression',
      path: ['field'],
    }),
  defaults: { field: '', label: '' },
  requiresSetup: true,
  group: 'inline',
  inline: true,
  atom: true,
  publish: 'snapshot',
  expressions: (a) =>
    [
      ...conditionSite(a.visibleWhen),
      ...(a.expression
        ? [{ path: ['expression'], source: a.expression, role: 'expression', context: 'record' }]
        : []),
    ] as ExprSite[],
  toMarkdown: (node) => `{{${String(node.attrs?.expression || node.attrs?.field || '')}}}`,
  toText: (node) => String(node.attrs?.label || node.attrs?.field || ''),
  aiDescription:
    'A field value or expression of the bound record inside a sentence ("Dear {{first_name}},").',
  label: { en: 'Inline value', de: 'Wert im Text' },
  description: { en: 'A field value inside a sentence', de: 'Ein Feldwert mitten im Satz' },
  category: 'data',
});

const recordFields = defineBlock({
  name: 'recordFields',
  supported: true,
  attrs: z
    .object({
      fields: z.array(DottedFieldPath).max(30),
      editable: z.boolean().optional(),
      hideEmpty: z.boolean().optional(),
      visibleWhen: PageBlockVisibility,
    })
    .strict(),
  defaults: { fields: [], editable: false },
  group: 'block',
  atom: true,
  publish: 'strip',
  expressions: (a) => conditionSite(a.visibleWhen),
  aiDescription: 'Display or edit a list of fields on the current record',
  label: { en: 'Record fields', de: 'Datensatzfelder' },
  description: {
    en: 'Display or edit a list of fields on the current record',
    de: 'Felder des aktuellen Datensatzes anzeigen oder bearbeiten',
  },
  category: 'data',
  palette: { order: 200 },
});

const calculationTable = defineBlock({
  name: 'calculationTable',
  supported: true,
  attrs: CalculationTable,
  defaults: {
    currency: 'EUR',
    columns: [
      { key: 'item', label: 'Item', format: 'text' },
      { key: 'value', label: 'Value', format: 'number', total: true },
    ],
    rows: [],
  },
  group: 'block',
  atom: true,
  publish: 'live',
  expressions: (a) =>
    a.columns.flatMap((c, i): ExprSite[] =>
      c.formula
        ? [
            {
              path: ['columns', i, 'formula'],
              source: c.formula,
              role: 'expression',
              context: 'row',
              expected: c.format === 'text' ? T_TEXT : T_NUMBER,
            },
          ]
        : [],
    ),
  toMarkdown: (node) => {
    const a = node.attrs as
      { columns?: { key: string; label: string }[]; rows?: Record<string, unknown>[] } | undefined;
    const cols = a?.columns ?? [];
    if (!cols.length) return '';
    return [
      `| ${cols.map((c) => escapeCell(c.label)).join(' | ')} |`,
      `| ${cols.map(() => '---').join(' | ')} |`,
      ...(a?.rows ?? []).map((r) => `| ${cols.map((c) => escapeCell(String(r[c.key] ?? ''))).join(' | ')} |`),
    ].join('\n');
  },
  aiDescription: 'Editable rows and columns with formulas, currencies and totals',
  label: { en: 'Calculated table', de: 'Berechnete Tabelle' },
  description: {
    en: 'Editable rows and columns with formulas, currencies and totals',
    de: 'Zeilen und Spalten mit Formeln, Währungen und Summen',
  },
  category: 'data',
  palette: { order: 210 },
  deprecated: { replacedBy: 'table' },
});

const form = defineBlock({
  name: 'form',
  supported: true,
  attrs: z
    .object({
      slug: z.union([z.literal(''), FormSlug]),
      public: z.boolean().optional(),
      visibleWhen: PageBlockVisibility,
    })
    .strict(),
  defaults: { slug: '', public: false },
  group: 'block',
  atom: true,
  publish: 'live',
  expressions: (a) => conditionSite(a.visibleWhen),
  aiDescription: 'Embed an existing form by slug; do not invent a slug',
  label: { en: 'Form', de: 'Formular' },
  description: {
    en: 'Embed an existing form by slug; do not invent a slug',
    de: 'Bettet ein vorhandenes Formular ein',
  },
  category: 'data',
  palette: { order: 220 },
});

/** The address of a meeting type (`/book/<slug>`); the same rule as the meeting type's own slug. */
const MeetingSlug = z.string().regex(/^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/, {
  error: 'Use lower-case letters, digits and hyphens',
});

const booking = defineBlock({
  name: 'booking',
  supported: true,
  attrs: z
    .object({
      slug: z.union([z.literal(''), MeetingSlug]),
      public: z.boolean().optional(),
      visibleWhen: PageBlockVisibility,
    })
    .strict(),
  defaults: { slug: '', public: false },
  group: 'block',
  atom: true,
  publish: 'live',
  expressions: (a) => conditionSite(a.visibleWhen),
  aiDescription:
    'Let visitors book a meeting of an existing meeting type by its address (slug); do not invent a slug',
  label: { en: 'Booking', de: 'Besprechungsbuchung' },
  description: {
    en: 'Let visitors pick a free time and book a meeting',
    de: 'Besucher wählen eine freie Zeit und buchen eine Besprechung',
  },
  category: 'data',
  palette: { order: 225 },
});

/**
 * A custom component (F20) on a page: which one and what each of its props gets from the page (a literal; a page has no
 * record, so expressions read the viewer and the clock). It runs for the signed-in viewer, so it never reaches a public page.
 */
const customComponent = defineBlock({
  name: 'customComponent',
  supported: true,
  attrs: z
    .object({
      componentId: z.string().max(64),
      inputs: ComponentInputs,
      visibleWhen: PageBlockVisibility,
    })
    .strict(),
  defaults: { componentId: '', inputs: {} },
  group: 'block',
  atom: true,
  publish: 'strip',
  expressions: (a) => conditionSite(a.visibleWhen),
  aiDescription: 'Show a published custom component of the workspace by its id; do not invent an id',
  label: { en: 'Component', de: 'Komponente' },
  description: {
    en: 'A component built in Settings › Components, running on this page',
    de: 'Eine unter Einstellungen › Komponenten gebaute Komponente, die auf dieser Seite läuft',
  },
  category: 'data',
  palette: { order: 230 },
});

const dataResult = defineBlock({
  name: 'dataResult',
  supported: true,
  attrs: DataResult,
  defaults: { title: '', mode: 'table', columns: [], rows: [], count: null, totals: {}, groups: [] },
  group: 'block',
  atom: true,
  publish: 'live',
  toMarkdown: (node) => {
    const a = node.attrs as DataResult | undefined;
    if (!a?.columns.length) return a?.title ? `*${a.title}*` : '';
    return [
      a.title ? `*${a.title}*\n` : '',
      `| ${a.columns.map((c) => escapeCell(c.label)).join(' | ')} |`,
      `| ${a.columns.map(() => '---').join(' | ')} |`,
      ...a.rows.map((r) => `| ${a.columns.map((c) => escapeCell(r[c.key] ?? '')).join(' | ')} |`),
    ].join('\n');
  },
  toText: (node) => String(node.attrs?.title ?? ''),
  aiDescription: 'A frozen result of a data view (public pages and snapshots).',
  label: { en: 'Data snapshot', de: 'Datenstand' },
});

const visual = defineBlock({
  name: 'visual',
  supported: true,
  attrs: VisualElement,
  defaults: {
    kind: 'metric',
    title: '',
    description: '',
    value: '',
    label: '',
    items: [],
    progress: 0,
    theme: 'studio',
    variant: 'soft',
  },
  group: 'block',
  atom: true,
  publish: 'live',
  toText: (node) => [node.attrs?.title, node.attrs?.value, node.attrs?.label].filter(Boolean).join(' '),
  toMarkdown: (node) =>
    [node.attrs?.title, node.attrs?.value, node.attrs?.label].filter(Boolean).map(String).join(' · '),
  aiDescription: 'Designed metric, quote, steps, chart, progress or comparison; static values only',
  label: { en: 'Visual', de: 'Visualisierung' },
  description: {
    en: 'Designed metric, quote, steps, chart, progress or comparison; static values only',
    de: 'Gestaltete Kennzahl, Zitat, Schritte, Diagramm, Fortschritt oder Vergleich',
  },
  category: 'visuals',
  palette: { order: 130, compact: true },
});

const barcode = defineBlock({
  name: 'barcode',
  attrs: z
    .object({
      ...codeProps,
      /** Width in px on the page. */
      size: z.number().int().min(48).max(800).default(160),
      align: z.enum(['left', 'center', 'right']).default('left'),
    })
    .strict()
    .superRefine(refineCode),
  defaults: { symbology: 'qr', value: '', size: 160, align: 'left' },
  requiresSetup: true,
  group: 'block',
  atom: true,
  publish: 'snapshot',
  expressions: (a) => codeExprSites(a.value),
  toText: (node) => (typeof node.attrs?.value === 'string' ? node.attrs.value : ''),
  degrade: (node) =>
    typeof node.attrs?.value === 'string' && node.attrs.value
      ? [{ type: 'paragraph', content: [{ type: 'text', text: node.attrs.value }] }]
      : [],
  aiDescription:
    'A QR code or barcode (code128, ean13, datamatrix, pdf417) of a static value or an expression.',
  label: { en: 'QR code or barcode', de: 'QR-Code oder Barcode' },
  description: {
    en: 'Encode a link, an id or a field value',
    de: 'Kodiert einen Link, eine Kennung oder einen Feldwert',
  },
  category: 'data',
});

const uiBlock = defineBlock({
  name: 'uiBlock',
  supported: true,
  attrs: z
    .object({
      id: z.string().max(80),
      component: z.string().max(100),
      props: z.record(z.string(), z.unknown()),
    })
    .strict(),
  defaults: { id: '', component: '', props: {} },
  group: 'block',
  atom: true,
  publish: 'strip',
  aiDescription: 'A registered record interface component using its supplied configuration schema',
  label: { en: 'Record component', de: 'Datensatz-Komponente' },
  description: {
    en: 'A registered record interface component using its supplied configuration schema',
    de: 'Eine registrierte Komponente der Datensatzansicht',
  },
  category: 'interface',
  palette: { order: 230, recordInterface: true },
});

// -------------------------------------------------------------------------------------------------
// Canvas v1 (presentations and PDF pages stored as block documents)
// -------------------------------------------------------------------------------------------------

const canvasPage = defineBlock({
  name: 'canvasPage',
  supported: true,
  attrs: CanvasFrame,
  defaults: { id: 'frame', title: '', width: 1280, height: 720, background: '#ffffff' },
  content: 'canvasItem*',
  publish: 'live',
  aiDescription: 'One slide or printed page of a canvas document (version 1).',
  label: { en: 'Canvas page', de: 'Canvas-Seite' },
  validate: (node, attrs, ctx) => {
    const children = node.content ?? [];
    if (children.length > 100) ctx.fail('A canvas supports up to 100 elements', 'canvas.elements');
    for (const child of children) {
      const item = child?.attrs ?? {};
      if (
        Number(item.x) + Number(item.width) > attrs.width + 0.1 ||
        Number(item.y) + Number(item.height) > attrs.height + 0.1
      )
        ctx.fail('Elements must fit inside the canvas', 'canvas.bounds');
    }
  },
});

const canvasItem = defineBlock({
  name: 'canvasItem',
  supported: true,
  attrs: CanvasItem,
  defaults: {
    id: 'item',
    x: 0,
    y: 0,
    width: 100,
    height: 100,
    fontSize: 28,
    fontFamily: 'Arial',
    color: '#172f35',
    background: 'transparent',
    bold: false,
    italic: false,
    align: 'left',
    shape: 'rectangle',
    border: '#cbd5d5',
    borderWidth: 0,
  },
  content: '(paragraph | image | dataView | dataResult | calculationTable | recordValue | visual)?',
  publish: 'live',
  contentMessage: 'An element supports one content block',
  aiDescription: 'A positioned box on a canvas page holding at most one block.',
  label: { en: 'Canvas element', de: 'Canvas-Element' },
  validate: (_node, attrs, ctx) => {
    const padding = attrs.padding ?? 0;
    if (padding * 2 >= attrs.width || padding * 2 >= attrs.height)
      ctx.fail('Padding must leave room for content', 'canvas.padding');
  },
});

/** Every block of a block document, in registry order. */
export const blockDefinitions: readonly BlockDefinition[] = [
  doc,
  paragraph,
  text,
  hardBreak,
  heading,
  bulletList,
  orderedList,
  listItem,
  taskList,
  taskItem,
  blockquote,
  codeBlock,
  horizontalRule,
  callout,
  toggle,
  details,
  detailsSummary,
  detailsContent,
  spacer,
  columns,
  column,
  section,
  tabs,
  tab,
  table,
  tableRow,
  tableCell,
  tableHeader,
  image,
  file,
  embed,
  pageLink,
  tableOfContents,
  mention,
  liveView,
  pageInsight,
  pageMetric,
  insightSnapshot,
  repeater,
  dataView,
  recordValue,
  inlineValue,
  recordFields,
  calculationTable,
  form,
  booking,
  customComponent,
  dataResult,
  visual,
  barcode,
  uiBlock,
  canvasPage,
  canvasItem,
] as unknown as readonly BlockDefinition[]; // each definition has its own attribute type; the registry stores them erased

// -------------------------------------------------------------------------------------------------
// Marks
// -------------------------------------------------------------------------------------------------

const weightBold = (el: HtmlElementView) => {
  const w = el.style['font-weight'];
  return w === 'bold' || w === 'bolder' || Number(w) >= 600;
};

/** Text and highlight colours are stored as tokens, never as CSS values. */
const ColorTokenAttr = z.union([z.enum(NAMED_COLORS), z.enum(THEME_COLOR_ROLES)]).nullish();

export const markDefinitions: readonly MarkDefinition[] = [
  defineMark({
    name: 'bold',
    supported: true,
    attrs: z.object({}).strict(),
    defaults: {},
    fromHTML: [
      { tag: 'strong' },
      { tag: 'b', match: (el) => el.style['font-weight'] !== 'normal' },
      { tag: '*', match: weightBold },
    ],
    markdown: { open: '**', close: '**' },
    label: { en: 'Bold', de: 'Fett' },
  }),
  defineMark({
    name: 'italic',
    supported: true,
    attrs: z.object({}).strict(),
    defaults: {},
    fromHTML: [{ tag: 'em' }, { tag: 'i' }, { tag: '*', match: (el) => el.style['font-style'] === 'italic' }],
    markdown: { open: '_', close: '_' },
    label: { en: 'Italic', de: 'Kursiv' },
  }),
  defineMark({
    name: 'underline',
    supported: true,
    attrs: z.object({}).strict(),
    defaults: {},
    fromHTML: [
      { tag: 'u' },
      { tag: '*', match: (el) => /underline/.test(el.style['text-decoration'] ?? '') },
    ],
    label: { en: 'Underline', de: 'Unterstrichen' },
  }),
  defineMark({
    name: 'strike',
    supported: true,
    attrs: z.object({}).strict(),
    defaults: {},
    fromHTML: [
      { tag: 's' },
      { tag: 'del' },
      { tag: 'strike' },
      { tag: '*', match: (el) => /line-through/.test(el.style['text-decoration'] ?? '') },
    ],
    markdown: { open: '~~', close: '~~' },
    label: { en: 'Strikethrough', de: 'Durchgestrichen' },
  }),
  defineMark({
    name: 'code',
    supported: true,
    attrs: z.object({}).strict(),
    defaults: {},
    fromHTML: [{ tag: 'code' }],
    markdown: { open: '`', close: '`' },
    label: { en: 'Inline code', de: 'Code im Text' },
  }),
  defineMark({
    name: 'link',
    supported: true,
    attrs: z
      .object({
        href: z.string().max(2000).refine(safeDocumentUrl, 'Invalid link'),
        target: z.enum(['_blank', '_self']).nullish(),
        rel: z.string().max(100).nullish(),
        class: z.null().optional(),
        title: z.string().max(300).nullish(),
      })
      .strict(),
    defaults: { href: '/', target: '_blank', rel: 'noopener noreferrer nofollow', class: null, title: null },
    fromHTML: [{ tag: 'a', match: (el) => Boolean(el.attrs.href), attrs: (el) => ({ href: el.attrs.href }) }],
    markdown: { open: '[', close: (a) => `](${a.href})` },
    label: { en: 'Link', de: 'Link' },
  }),
  defineMark({
    name: 'textStyle',
    supported: true,
    attrs: z.object({ color: ColorTokenAttr }).strict(),
    defaults: { color: null },
    fromHTML: [
      {
        tag: '*',
        match: (el) => Boolean(el.style.color),
        attrs: (el) => ({ color: nearestNamedColor(cssColorToHex(el.style.color ?? '') ?? '') }),
      },
      {
        tag: 'font',
        match: (el) => Boolean(el.attrs.color),
        attrs: (el) => ({ color: nearestNamedColor(cssColorToHex(el.attrs.color ?? '') ?? '') }),
      },
    ],
    label: { en: 'Text colour', de: 'Textfarbe' },
  }),
  defineMark({
    name: 'highlight',
    supported: true,
    attrs: z.object({ color: ColorTokenAttr }).strict(),
    defaults: { color: 'yellow' },
    fromHTML: [
      { tag: 'mark', attrs: () => ({ color: 'yellow' }) },
      {
        tag: '*',
        match: (el) => Boolean(el.style['background-color'] ?? el.style.background),
        attrs: (el) => ({
          color: nearestNamedColor(
            cssColorToHex(el.style['background-color'] ?? el.style.background ?? '') ?? '',
          ),
        }),
      },
    ],
    label: { en: 'Highlight', de: 'Hervorhebung' },
  }),
] as unknown as readonly MarkDefinition[]; // each definition has its own attribute type; the registry stores them erased
